import { useEffect, useState } from 'react'
import AdminShell from './AdminShell'
import { tournamentApi } from '../../services/api'

/**
 * Invite management.  Sub-tabs by status (pending / accepted / expired).
 * "New invite" form modal opens with email + reseller name + optional TTL.
 */
export default function ResellerInvites() {
  const [status, setStatus] = useState('pending')
  const [invites, setInvites] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [showCreate, setShowCreate] = useState(false)
  const [recentToken, setRecentToken] = useState(null) // { url } shown after create

  function reload() {
    setLoading(true)
    setError(null)
    tournamentApi.get(`/admin/resellers/invites?status=${status}`)
      .then(r => setInvites(r.data.invites || []))
      .catch(err => setError(err?.response?.data?.message || err.message))
      .finally(() => setLoading(false))
  }
  useEffect(reload, [status])

  async function handleCancel(id) {
    if (!confirm('Cancel this invite? The recipient will no longer be able to use it.')) return
    try {
      await tournamentApi.delete(`/admin/resellers/invites/${id}`)
      reload()
    } catch (err) {
      alert(err?.response?.data?.message || err.message)
    }
  }

  async function handleResend(id) {
    try {
      const { data } = await tournamentApi.post(`/admin/resellers/invites/${id}/resend`, {})
      setRecentToken({ url: data.url, kind: 'resent' })
      reload()
    } catch (err) {
      alert(err?.response?.data?.message || err.message)
    }
  }

  return (
    <AdminShell title="Reseller Invites">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div style={{ display: 'flex', gap: 4, background: '#fff', borderRadius: 8, padding: 4, border: '1px solid #e2e8f0' }}>
          {['pending', 'accepted', 'expired'].map(s => (
            <button key={s} onClick={() => setStatus(s)} style={tabBtn(s === status)}>
              {s[0].toUpperCase() + s.slice(1)}
            </button>
          ))}
        </div>
        <button onClick={() => setShowCreate(true)} style={primaryBtn}>+ New invite</button>
      </div>

      {recentToken && (
        <div style={{ padding: 16, marginBottom: 16, background: '#ecfdf5', border: '1px solid #a7f3d0', borderRadius: 8 }}>
          <div style={{ fontWeight: 600, color: '#065f46', marginBottom: 6 }}>
            Invite {recentToken.kind === 'resent' ? 'resent' : 'created'}.
          </div>
          <div style={{ fontSize: 13, color: '#047857', wordBreak: 'break-all' }}>
            Magic link: <code style={{ background: '#fff', padding: '2px 6px', borderRadius: 4 }}>{recentToken.url}</code>
          </div>
          <button onClick={() => navigator.clipboard.writeText(recentToken.url)} style={{ ...secondaryBtn, marginTop: 8 }}>Copy link</button>
          <button onClick={() => setRecentToken(null)} style={{ ...secondaryBtn, marginTop: 8, marginLeft: 8 }}>Dismiss</button>
        </div>
      )}

      {error && <div style={errorBox}>{error}</div>}
      {loading
        ? <div style={empty}>Loading…</div>
        : invites.length === 0
          ? <div style={empty}>No {status} invites.</div>
          : (
            <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, overflow: 'hidden' }}>
              <table style={tbl}>
                <thead>
                  <tr style={tr}>
                    <th style={th}>Email</th>
                    <th style={th}>Reseller</th>
                    <th style={th}>Type</th>
                    <th style={th}>Sent by</th>
                    <th style={th}>Created</th>
                    <th style={th}>Expires / Accepted</th>
                    <th style={th}></th>
                  </tr>
                </thead>
                <tbody>
                  {invites.map(i => (
                    <tr key={i.ID_NO} style={tr}>
                      <td style={td}>{i.Email}</td>
                      <td style={td}>{i.ResellerName}</td>
                      <td style={td}>{i.IsTeamInvite ? <Badge color="#3b82f6">Team</Badge> : <Badge color="#10b981">Account</Badge>}</td>
                      <td style={td}>{i.InviterFirstName} {i.InviterLastName}<br/><span style={{ fontSize: 12, color: '#64748b' }}>{i.InviterEmail}</span></td>
                      <td style={td}>{new Date(i.CreatedAt).toLocaleString()}</td>
                      <td style={td}>{i.AcceptedAt ? <span style={{ color: '#16a34a' }}>Accepted {new Date(i.AcceptedAt).toLocaleDateString()}</span> : new Date(i.ExpiresAt).toLocaleDateString()}</td>
                      <td style={td}>
                        {status === 'pending' && (
                          <>
                            <button onClick={() => handleResend(i.ID_NO)} style={secondaryBtn}>Resend</button>
                            <button onClick={() => handleCancel(i.ID_NO)} style={{ ...secondaryBtn, marginLeft: 6, color: '#dc2626', borderColor: '#fecaca' }}>Cancel</button>
                          </>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
      }

      {showCreate && <CreateInviteModal
        onClose={() => setShowCreate(false)}
        onCreated={(data) => {
          setShowCreate(false)
          setRecentToken({ url: data.invite.url, kind: 'created' })
          setStatus('pending')
          reload()
        }}
      />}
    </AdminShell>
  )
}

function CreateInviteModal({ onClose, onCreated }) {
  const [email, setEmail] = useState('')
  const [resellerName, setResellerName] = useState('')
  const [expiresInDays, setExpiresInDays] = useState(14)
  const [submitting, setSubmitting] = useState(false)
  const [err, setErr] = useState(null)

  async function handleSubmit(e) {
    e.preventDefault()
    setErr(null)
    setSubmitting(true)
    try {
      const { data } = await tournamentApi.post('/admin/resellers/invites', {
        email, resellerName, expiresInDays: Number(expiresInDays),
      })
      onCreated(data)
    } catch (e2) {
      setErr(e2?.response?.data?.message || e2.message)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div style={overlay} onClick={onClose}>
      <div style={modalCard} onClick={e => e.stopPropagation()}>
        <h3 style={{ margin: '0 0 16px', fontSize: 18, color: '#0f172a' }}>Invite a new Reseller account</h3>
        {err && <div style={errorBox}>{err}</div>}
        <form onSubmit={handleSubmit}>
          <Field label="Reseller name" hint="Shown in the invite email and used to generate the URL slug.">
            <input type="text" value={resellerName} onChange={e => setResellerName(e.target.value)} required style={inputStyle} placeholder="Reseller company name" />
          </Field>
          <Field label="Contact email" hint="The first user (account owner) — they will receive the magic link.">
            <input type="email" value={email} onChange={e => setEmail(e.target.value)} required style={inputStyle} placeholder="founder@reseller.com" />
          </Field>
          <Field label="Expires in (days)">
            <input type="number" min={1} max={90} value={expiresInDays} onChange={e => setExpiresInDays(e.target.value)} style={{ ...inputStyle, width: 120 }} />
          </Field>
          <div style={{ display: 'flex', gap: 8, marginTop: 20, justifyContent: 'flex-end' }}>
            <button type="button" onClick={onClose} style={secondaryBtn}>Cancel</button>
            <button type="submit" disabled={submitting} style={{ ...primaryBtn, opacity: submitting ? 0.6 : 1 }}>
              {submitting ? 'Sending…' : 'Send invite'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

function Field({ label, hint, children }) {
  return (
    <div style={{ marginBottom: 16 }}>
      <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#1e293b', marginBottom: 6 }}>{label}</label>
      {children}
      {hint && <div style={{ fontSize: 12, color: '#64748b', marginTop: 4 }}>{hint}</div>}
    </div>
  )
}

function Badge({ color, children }) {
  return <span style={{ padding: '3px 8px', background: color + '15', color, borderRadius: 999, fontSize: 11, fontWeight: 600 }}>{children}</span>
}

const errorBox = { padding: 12, background: '#fef2f2', color: '#991b1b', border: '1px solid #fecaca', borderRadius: 6, marginBottom: 16 }
const empty = { padding: 40, textAlign: 'center', color: '#64748b', background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8 }
const tbl = { width: '100%', borderCollapse: 'collapse', fontSize: 14 }
const tr = { borderBottom: '1px solid #f1f5f9' }
const th = { padding: '12px 16px', textAlign: 'left', fontWeight: 600, color: '#64748b', fontSize: 12, letterSpacing: 0.5, textTransform: 'uppercase', background: '#f8fafc' }
const td = { padding: '14px 16px', verticalAlign: 'top' }
const inputStyle = { width: '100%', padding: '10px 12px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 14, fontFamily: 'inherit', boxSizing: 'border-box' }
const primaryBtn = { padding: '10px 20px', background: '#0f172a', color: '#fff', border: 'none', borderRadius: 6, fontSize: 14, fontWeight: 600, cursor: 'pointer' }
const secondaryBtn = { padding: '6px 12px', background: '#fff', color: '#475569', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 13, cursor: 'pointer' }
const tabBtn = (active) => ({ padding: '8px 16px', background: active ? '#0f172a' : 'transparent', color: active ? '#fff' : '#64748b', border: 'none', borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: 'pointer' })
const overlay = { position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20 }
const modalCard = { background: '#fff', borderRadius: 12, padding: 28, width: '100%', maxWidth: 500, boxShadow: '0 20px 50px rgba(0,0,0,0.2)' }
